import { DataTable } from '@juvare/common-data-table-ui/DataTable';
import { LayoutRows3 } from '@juvare/common-web-ui/LayoutRows3';
import { Divider } from '@mui/material';
import React from 'react';
import { getDefaultPermissions } from '../../../../backend/features/permissions';
import { useFileTable, useSelection } from '../../../hooks';
import { DocumentLibraryContext } from '../../../providers';
import { useSelectFilesDialogColumns } from '../hooks/useSelectFilesDialogColumns';
import { useSelectFilesDialogNavigation } from '../hooks/useSelectFilesDialogNavigation';
import { SelectFilesDialogFooter } from './SelectFilesDialogFooter';
import { SelectFilesDialogHeader } from './SelectFilesDialogHeader';
import { SelectFilesDialogNavigation } from './SelectFilesDialogNavigation';
/**
 * A React component that provides a dialog interface for selecting files.
 * It allows users to navigate directories, select files with optional restrictions,
 * and proceed with the selected files via a customizable interface.
 *
 * @example
 * <SelectFilesDialog
 *   allowedFileTypes={['text/plain']}
 *   content={{
 *       selectButtonLabel: 'Select',
 *       dialogTitle: 'Select Files',
 *   }}
 *   maxFilesCount={5}
 *   maxFilesSize={5000000}
 *   preselectedFilesCount={2}
 *   preselectedFilesSize={1000}
 *   rootDirectoryId='bb49ae68-c8e1-46d8-9f70-22dcf6a6701f'
 *   onClose={() => console.log('Dialog closed')}
 *   onFilesSelect={async (selectedFiles) => console.log('Selected files:', selectedFiles)}
 * />
 */
export const SelectFilesDialog = ({ allowedFileTypes, content, maxFilesCount, maxFilesSize, preselectedFilesCount, preselectedFilesSize, rootDirectoryId, onClose, onFilesSelect, }) => {
    const documentLibraryContext = React.useContext(DocumentLibraryContext);
    const { handleNavigateTo, handleNavigateBack } = useSelectFilesDialogNavigation({ rootDirectoryId });
    const getIdentifier = (file) => file.id;
    const { selected, isSelected, selectOne, selectMany, deselectOne, deselectMany, deselectAll } = useSelection({
        getIdentifier,
    });
    const mapRowToFileSelection = (row) => {
        var _a;
        return ({
            id: row.id,
            currentVersionId: row.columns.currentVersionId,
            name: row.columns.name,
            size: (_a = row.columns.contentLength) !== null && _a !== void 0 ? _a : 0,
            contentType: row.columns.type,
        });
    };
    React.useEffect(() => {
        handleNavigateTo(rootDirectoryId);
        // Rule disabled because intention is to open root directory only on initial load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { dataTableProps } = useFileTable({
        defaultPermissions: getDefaultPermissions(),
        currentDirectoryId: documentLibraryContext.currentDirectoryId,
        navigate: handleNavigateTo,
    });
    const isRowSelectable = (row) => {
        const isFileTypeAllowed = !allowedFileTypes || allowedFileTypes.includes(row.columns.type);
        return !row.columns.directory && row.columns.currentVersionId !== undefined && isFileTypeAllowed;
    };
    const handleRowNavigationOrSelection = (row) => {
        if (row.columns.directory) {
            handleNavigateTo(row.id);
        }
        else if (isSelected(mapRowToFileSelection(row))) {
            deselectOne(mapRowToFileSelection(row));
        }
        else if (isRowSelectable(row) && !isSelected(mapRowToFileSelection(row))) {
            selectOne(mapRowToFileSelection(row));
        }
    };
    const handleSelectAll = (rows, newValue) => {
        if (newValue) {
            selectMany(rows.map(mapRowToFileSelection));
        }
        else {
            deselectMany(rows.map(mapRowToFileSelection));
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(LayoutRows3, { row1: React.createElement(SelectFilesDialogHeader, { onClose: () => onClose(), title: content.dialogTitle }), row2: React.createElement(React.Fragment, null,
                React.createElement(SelectFilesDialogNavigation, { onNavigateTo: handleNavigateTo, onNavigateBack: handleNavigateBack, rootDirectoryId: rootDirectoryId }),
                React.createElement(DataTable, Object.assign({}, dataTableProps, { columns: useSelectFilesDialogColumns(), rowSelectionEnabled: true, isRowSelectable: isRowSelectable, selectedRowIds: selected.map(getIdentifier), onRowClick: handleRowNavigationOrSelection, onRowSelectionChange: (row, selected) => selected ? selectOne(mapRowToFileSelection(row)) : deselectOne(mapRowToFileSelection(row)), onSelectAllRowsInCurrentPageClick: handleSelectAll, onDeselectAllRows: deselectAll }))), row3: React.createElement(React.Fragment, null,
                React.createElement(Divider, null),
                React.createElement(SelectFilesDialogFooter, { selectButtonLabel: content.selectButtonLabel, maxFilesCount: maxFilesCount, maxFilesSize: maxFilesSize, preselectedFilesCount: preselectedFilesCount, preselectedFilesSize: preselectedFilesSize, selectedFiles: selected, onFilesSelect: () => onFilesSelect(selected) })), fullHeightRow: 'row2', row2Scrollable: true })));
};
